import { Dispatch, Middleware, MiddlewareAPI } from 'redux';
import { LOCATION_CHANGED } from 'redux-little-router';

import { Action } from '../../actions';
import {
    RENDER_ON_FINISHED,
    RenderOnFinished
} from '../../actions/grid-screen';
import { mapCampaignTypeToCalcType } from '../../mappers/filter/filter';
import { RoutesPath } from '../../routes/index';
import { selectGridSourceParamsViewForRum } from '../../selectors/rum';
import { campaigns } from '../../typings/common';
import { CampaignsCalcTypeFilter } from '../../typings/domain';
import { GridViewType } from '../../typings/grid';
import { State } from '../../typings/state';

import { RUM, rumCounters } from './RUM';

export const rumMiddleWare: Middleware<{}, State, Dispatch<Action>> = (
    store: MiddlewareAPI<Dispatch<Action>, State>
): ((next: Dispatch<Action>) => (action: Action) => Action) => (
    next: Dispatch<Action>
): ((action: Action) => Action) => {
    if (!RUM.isRumInited()) {
        return next;
    }

    return (action: Action): Action => {
        const oldState = store.getState();
        const result = next(action);
        const newState = store.getState();

        processAction(oldState as State, newState as State, action);

        return result;
    };
};

// Флаг первой загрузки приложения.
let isFirstLoad = true;
let isSourceParamsFirstUpdate = true;

const processAction = (
    oldState: State,
    newState: State,
    action: Action
): void => {
    switch (action.type) {
        case LOCATION_CHANGED: {
            const { route } = newState.router;

            switch (route) {
                case RoutesPath.shortGrid:
                case RoutesPath.grid: {
                    const newView = selectGridSourceParamsViewForRum(newState);
                    const oldView = selectGridSourceParamsViewForRum(oldState);

                    if (isSourceParamsFirstUpdate) {
                        isSourceParamsFirstUpdate = false;
                        break;
                    }

                    if (oldView !== newView) {
                        RUM.startViewTimer(newView as GridViewType, [
                            newView as GridViewType
                        ]);
                    }

                    break;
                }

                default:
                    break;
            }

            if (oldState.router.pathname !== newState.router.pathname) {
                // при заходе на /dna/grid?ulogin=XXX происходит replace, в этом случае не нужно сбрасывать флаг
                const isGridFirstVisit =
                    [RoutesPath.grid, RoutesPath.shortGrid].includes(
                        oldState.router.route
                    ) && !(oldState.router.params || {}).view;
                if (!isGridFirstVisit) {
                    isFirstLoad = false;
                }
            }

            if (
                isNewRoute(
                    oldState.router.route,
                    newState.router.route,
                    RoutesPath.recommendationsPage
                )
            ) {
                RUM.startViewTimer(rumCounters.dnaSPARecommendationsRender, [
                    rumCounters.dnaSPARecommendationsRender
                ]);
            }

            if (
                isNewRoute(
                    oldState.router.route,
                    newState.router.route,
                    RoutesPath.recommendationsItems
                )
            ) {
                RUM.startViewTimer(
                    rumCounters.dnaSPARecommendationsGridRender,
                    [rumCounters.dnaSPARecommendationsGridRender]
                );
            }

            if (
                isNewRoute(
                    oldState.router.route,
                    newState.router.route,
                    RoutesPath.bannersEdit
                )
            ) {
                const keysForGrafana = [
                    rumCounters.dnaSPABannersEditRender,
                    // Мы заранее не знаем точное значение calcTypeFilter
                    // (при переходе по прямой ссылке calcTypeFilter не верный),
                    // поэтому, начинаем отсчет для всех типов оплаты,
                    // чтобы после начался замер для нужного типа
                    rumCounters.dnaSPABannersEditCpmRender,
                    rumCounters.dnaSPABannersEditCpcRender
                ];

                if (newState.editBanners.bannersIds.length < 10) {
                    keysForGrafana.push(rumCounters.dnaSPABannersEditRender10);
                } else if (newState.editBanners.bannersIds.length < 50) {
                    keysForGrafana.push(rumCounters.dnaSPABannersEditRender50);
                } else if (newState.editBanners.bannersIds.length < 100) {
                    keysForGrafana.push(rumCounters.dnaSPABannersEditRender100);
                } else if (newState.editBanners.bannersIds.length < 500) {
                    keysForGrafana.push(rumCounters.dnaSPABannersEditRender500);
                } else if (newState.editBanners.bannersIds.length <= 1000) {
                    keysForGrafana.push(
                        rumCounters.dnaSPABannersEditRender1000
                    );
                }

                RUM.startViewTimer(
                    rumCounters.dnaSPABannersEditRender,
                    keysForGrafana
                );
            }

            if (
                isNewRoute(
                    oldState.router.route,
                    newState.router.route,
                    RoutesPath.groupsEdit
                )
            ) {
                const keysForGrafana = [
                    rumCounters.dnaSPAGroupsEditRender,
                    // Мы заранее не знаем точное значение calcTypeFilter
                    // (при переходе по прямой ссылке calcTypeFilter не верный),
                    // поэтому, начинаем отсчет для всех типов оплаты,
                    // чтобы после начался замер для нужного типа
                    rumCounters.dnaSPAGroupsEditCpmRender,
                    rumCounters.dnaSPAGroupsEditCpcRender
                ];

                if (newState.editGroups.groupsIds.length < 10) {
                    keysForGrafana.push(rumCounters.dnaSPAGroupsEditRender10);
                } else if (newState.editGroups.groupsIds.length < 50) {
                    keysForGrafana.push(rumCounters.dnaSPAGroupsEditRender50);
                } else if (newState.editGroups.groupsIds.length < 100) {
                    keysForGrafana.push(rumCounters.dnaSPAGroupsEditRender100);
                } else if (newState.editGroups.groupsIds.length < 500) {
                    keysForGrafana.push(rumCounters.dnaSPAGroupsEditRender500);
                } else if (newState.editGroups.groupsIds.length <= 1000) {
                    keysForGrafana.push(rumCounters.dnaSPAGroupsEditRender1000);
                }

                RUM.startViewTimer(
                    rumCounters.dnaSPAGroupsEditRender,
                    keysForGrafana
                );
            }

            break;
        }
        case RENDER_ON_FINISHED: {
            processRenderOnFinished(oldState, newState, action);
            break;
        }
        default: {
            return;
        }
    }
};

function isNewRoute(
    oldRoute: string,
    newRoute: string,
    path: RoutesPath
): boolean {
    return oldRoute !== path && newRoute === path;
}

function processRenderOnFinished(
    oldState: State,
    newState: State,
    action: RenderOnFinished
): void {
    const view = selectGridSourceParamsViewForRum(newState);
    const { route, params = {} } = newState.router;

    switch (route) {
        case RoutesPath.grid:
        case RoutesPath.shortGrid:
            if (isFirstLoad) {
                const keysForGrafana = [RUM.getFirstLoadKeyByView(view)];

                if (newState.grid.gridData.total < 10) {
                    keysForGrafana.push(
                        RUM.getFirstLoadKeyByViewLessThenTenItems(view)
                    );
                } else if (newState.grid.gridData.total < 50) {
                    keysForGrafana.push(
                        RUM.getFirstLoadKeyByViewLessThenFiftyItems(view)
                    );
                } else if (newState.grid.gridData.total < 100) {
                    keysForGrafana.push(
                        RUM.getFirstLoadKeyByViewLessThenHundredItems(view)
                    );
                } else if (newState.grid.gridData.total < 500) {
                    keysForGrafana.push(
                        RUM.getFirstLoadKeyByViewLessThenFiveHundredItems(view)
                    );
                } else if (newState.grid.gridData.total < 1000) {
                    keysForGrafana.push(
                        RUM.getFirstLoadKeyByViewLessThenThousandItems(view)
                    );
                } else if (newState.grid.gridData.total <= 10000) {
                    keysForGrafana.push(
                        RUM.getFirstLoadKeyByViewLessThenTenThousandItems(view)
                    );
                }

                RUM.sendFirstLoadTime(
                    RUM.getFirstLoadKeyByView(view),
                    keysForGrafana,
                    action.reqId
                );
                isFirstLoad = false;
            } else {
                RUM.endTimer(view || campaigns, [view || campaigns]);
            }
            break;
        case RoutesPath.recommendationsItems:
        case RoutesPath.recommendationsPage:
            if (isFirstLoad) {
                const key = RUM.getFirstLoadKeyByRoute(route, params.type);

                if (key) {
                    RUM.sendFirstLoadTime(key, [key], action.reqId);
                }
                isFirstLoad = false;
            } else {
                if (RoutesPath.recommendationsItems === route) {
                    RUM.endTimer(rumCounters.dnaSPARecommendationsRender, [
                        rumCounters.dnaSPARecommendationsRender
                    ]);
                } else {
                    RUM.endTimer(rumCounters.dnaSPARecommendationsGridRender, [
                        rumCounters.dnaSPARecommendationsGridRender
                    ]);
                }
            }
            break;
        case RoutesPath.bannersEdit:
            const banner =
                newState.editBanners.banners[
                    newState.editBanners.bannersIds[0]
                ];
            const isCpmBanner = Boolean(
                banner &&
                    mapCampaignTypeToCalcType(banner.campaign.type) ===
                        CampaignsCalcTypeFilter.CPM
            );

            if (isFirstLoad) {
                const keysForGrafana = [
                    rumCounters.bannersEditFirst,
                    isCpmBanner
                        ? rumCounters.bannersEditCpmFirst
                        : rumCounters.bannersEditCpcFirst
                ];

                if (newState.editBanners.bannersIds.length < 10) {
                    keysForGrafana.push(rumCounters.bannersEditFirst10);
                } else if (newState.editBanners.bannersIds.length < 50) {
                    keysForGrafana.push(rumCounters.bannersEditFirst50);
                } else if (newState.editBanners.bannersIds.length < 100) {
                    keysForGrafana.push(rumCounters.bannersEditFirst100);
                } else if (newState.editBanners.bannersIds.length < 500) {
                    keysForGrafana.push(rumCounters.bannersEditFirst500);
                } else if (newState.editBanners.bannersIds.length <= 1000) {
                    keysForGrafana.push(rumCounters.bannersEditFirst1000);
                }

                RUM.sendFirstLoadTime(
                    rumCounters.bannersEditFirst,
                    keysForGrafana,
                    action.reqId
                );
                isFirstLoad = false;
            } else {
                const keysForGrafana = [
                    rumCounters.dnaSPABannersEditRender,
                    isCpmBanner
                        ? rumCounters.dnaSPABannersEditCpmRender
                        : rumCounters.dnaSPABannersEditCpcRender
                ];

                if (newState.editBanners.bannersIds.length < 10) {
                    keysForGrafana.push(rumCounters.dnaSPABannersEditRender10);
                } else if (newState.editBanners.bannersIds.length < 50) {
                    keysForGrafana.push(rumCounters.dnaSPABannersEditRender50);
                } else if (newState.editBanners.bannersIds.length < 100) {
                    keysForGrafana.push(rumCounters.dnaSPABannersEditRender100);
                } else if (newState.editBanners.bannersIds.length < 500) {
                    keysForGrafana.push(rumCounters.dnaSPABannersEditRender500);
                } else if (newState.editBanners.bannersIds.length <= 1000) {
                    keysForGrafana.push(
                        rumCounters.dnaSPABannersEditRender1000
                    );
                }

                RUM.endTimer(
                    rumCounters.dnaSPABannersEditRender,
                    keysForGrafana
                );
            }
            break;
        case RoutesPath.groupsEdit:
            const group =
                newState.editGroups.groups[newState.editGroups.groupsIds[0]];
            const isCpmGroup = Boolean(
                group &&
                    mapCampaignTypeToCalcType(group.value.campaignType) ===
                        CampaignsCalcTypeFilter.CPM
            );

            if (isFirstLoad) {
                const keysForGrafana = [
                    rumCounters.groupsEditFirst,
                    isCpmGroup
                        ? rumCounters.groupsEditCpmFirst
                        : rumCounters.groupsEditCpcFirst
                ];

                if (newState.editGroups.groupsIds.length < 10) {
                    keysForGrafana.push(rumCounters.groupsEditFirst10);
                } else if (newState.editGroups.groupsIds.length < 50) {
                    keysForGrafana.push(rumCounters.groupsEditFirst50);
                } else if (newState.editGroups.groupsIds.length < 100) {
                    keysForGrafana.push(rumCounters.groupsEditFirst100);
                } else if (newState.editGroups.groupsIds.length < 500) {
                    keysForGrafana.push(rumCounters.groupsEditFirst500);
                } else if (newState.editGroups.groupsIds.length <= 1000) {
                    keysForGrafana.push(rumCounters.groupsEditFirst1000);
                }

                RUM.sendFirstLoadTime(
                    rumCounters.groupsEditFirst,
                    keysForGrafana,
                    action.reqId
                );
                isFirstLoad = false;
            } else {
                const keysForGrafana = [
                    rumCounters.dnaSPAGroupsEditRender,
                    isCpmGroup
                        ? rumCounters.dnaSPAGroupsEditCpmRender
                        : rumCounters.dnaSPAGroupsEditCpcRender
                ];

                if (newState.editGroups.groupsIds.length < 10) {
                    keysForGrafana.push(rumCounters.dnaSPAGroupsEditRender10);
                } else if (newState.editGroups.groupsIds.length < 50) {
                    keysForGrafana.push(rumCounters.dnaSPAGroupsEditRender50);
                } else if (newState.editGroups.groupsIds.length < 100) {
                    keysForGrafana.push(rumCounters.dnaSPAGroupsEditRender100);
                } else if (newState.editGroups.groupsIds.length < 500) {
                    keysForGrafana.push(rumCounters.dnaSPAGroupsEditRender500);
                } else if (newState.editGroups.groupsIds.length <= 1000) {
                    keysForGrafana.push(rumCounters.dnaSPAGroupsEditRender1000);
                }

                RUM.endTimer(
                    rumCounters.dnaSPAGroupsEditRender,
                    keysForGrafana
                );
            }
            break;
        default:
            break;
    }
}
